'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

// 翻译数据类型
type TranslationData = {
  [key: string]: any
}

type I18nContextType = {
  language: 'zh' | 'en'
  setLanguage: (lang: 'zh' | 'en', callback?: () => void) => void
  t: (key: string) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

// 翻译数据
const translations: Record<'zh' | 'en', TranslationData> = {
  zh: {
    app: {
      title: "Trigger Detector",
      subtitle: "anonymous@anonymous"
    },
    navigation: {
      features: "功能特点",
      howItWorks: "工作原理",
      language: "中文",
      logout: "登出"
    },
    hero: {
      titleLoggedOut: "社交媒体内容常常让你感到焦虑？",
      subtitleLoggedOut: "你是否觉得被困在了焦虑信息茧房里？\n上传截图，和 AI 一起探索你的焦虑来源",
      subtitle1LoggedOut: "你是否觉得被困在了焦虑信息茧房里？",
      subtitle2LoggedOut: "上传截图，和 AI 一起探索你的焦虑来源",
      titleLoggedIn: "社交媒体内容常常让你感到焦虑？",
      subtitleLoggedIn: "你是否觉得被困在了焦虑信息茧房里？\n上传截图，和 AI 一起探索你的焦虑来源",
      subtitle1LoggedIn: "你是否觉得被困在了焦虑信息茧房里？",
      subtitle2LoggedIn: "上传截图，和 AI 一起探索你的焦虑来源",
      startButton: "开始分析"
    },
    login: {
      title: "使用体验码快速体验",
      placeholder: "输入体验码…",
      startButton: "登录",
      testKey: "测试密钥",
      testExperienceCodeLabel: "测试体验码：",
      copy: "复制",
      copied: "已复制",
      copyAndFill: "复制并填入",
      verifying: "正在验证您的体验码，请稍候...",
      userNotFound: "用户不存在，请检查您的会话密钥",
      loginFailed: "登录失败，请检查您的会话密钥或网络连接"
    },
    howItWorks: {
      title: "预览体验流程",
      simpleTitle: "体验流程",
      step1: {
        title: "填写信息",
        titleSimple: "登录",
        description: "分享你的基本信息，让AI更好地理解你的独特感受"
      },
      step2: {
        title: "分享困扰",
        titleSimple: "截图并上传",
        description: "截取让你感到困扰的社交媒体内容并上传，让AI进行分析"
      },
      step3: {
        title: "理解焦虑",
        titleSimple: "选取焦虑原因",
        description: "从AI分析结果中选取符合你感受的焦虑触发因素"
      },
      step4: {
        title: "获得帮助",
        titleSimple: "获取缓解建议",
        description: "自动获取个性化的焦虑缓解建议，学会更好地应对类似情况"
      }
    },
    userNotice: {
      title: "欢迎使用Trigger Detector",
      introduction: "本工具将结合您提供的背景信息和上传的社交媒体截图，使用人工智能技术识别可能的焦虑来源，并基于心理学理论生成个性化的情绪缓解建议。",
      process: {
        title: "使用流程简要：",
        step1: "使用体验码登录，填写基本信息",
        step2: "上传你在社交媒体上看到并感到焦虑的截图",
        step3: "AI分析结果里选择或调整符合你感受的焦虑的原因",
        step4: "提交后，系统将展示为你定制的情绪缓解建议"
      },
      privacy: {
        title: "数据用途与隐私保护",
        item1: "数据将用于科研研究目的，例如了解社交媒体使用与焦虑之间的关系，改进心理健康干预工具等。",
        item2: "我们收集的数据包括你上传的截图、选择结果与评价反馈。不会收集你的姓名等直接身份信息。联系方式仅用于抽奖活动和访谈参与。",
        item3: "所有数据将匿名化处理并安全存储在符合数据保护规定的服务器上，仅限研究团队使用。",
        item4: "研究项目已获得纽卡斯尔大学伦理审批。"
      },
      consent: {
        title: "点击‘开始体验’即表示你：",
        item1: "已阅读并理解上述说明；",
        item2: "同意你提供的数据被用于科研目的；",
        item3: "明确知晓你可以在任何时候退出，不再提供信息；",
        item4: "年满 18 岁，自愿参与本工具的使用与研究。"
      },
      startButton: "开始体验"
    },
    analysis: {
      title: "让AI陪你一起看看这个帖子",
      flowHint: "填写信息 → 截图上传 → 选取焦虑原因 → 获取缓解建议",
      basicInfo: {
        title: "基本信息",
        displayTitle: "您的基本信息",
        autoSaveHint: "💡 提示：填写基本信息后，在上传截图时会自动保存，无需手动点击保存按钮",
        loading: "加载中...",
        editButton: "编辑",
        gender: {
          label: "性别",
          male: "男",
          female: "女",
          other: "其他",
          preferNotToSay: "不愿透露"
        },
        age: {
          label: "年龄段",
          placeholder: "请选择年龄段",
          "18-24": "18–24 岁",
          "25-30": "25–30 岁",
          "31-35": "31–35 岁",
          "36-40": "36–40 岁",
          "41-50": "41–50岁",
          "51-60": "51–60 岁",
          "61-70": "61–70 岁",
          "71-80": "71–80岁",
          "80plus": "80 岁以上",
          "preferNotToSay": "不愿透露"
        },
        occupation: {
          label: "职业状态",
          placeholder: "请选择职业状态",
          collegeStudent: "大专/高职学生",
          undergraduate: "本科生",
          masterStudent: "研究生",
          phdStudent: "博士生",
          workingFullTime: "全职工作",
          workingPartTime: "兼职工作",
          freelancer: "自由职业者",
          entrepreneur: "创业者",
          unemployed: "暂未就业",
          fullTimeParent: "全职照护者（宝妈/宝爸/照护亲人）",
          retired: "退休",
          notInLaborForce: "非劳动力人口（不工作也不找工作）",
          other: "其他",
          preferNotToSay: "不愿透露"
        },
        mentalHealthHistory: {
          label: "你是否有过被专业人士诊断的心理问题",
          placeholder: "请选择",
          none: "未曾有过",
          diagnosedAnxiety: "曾被诊断为焦虑症",
          diagnosedDepression: "曾被诊断为抑郁症",
          diagnosedOther: "曾被诊断为其他心理问题",
          preferNotToSay: "不愿透露"
        },
        otherInfo: {
          label: "其他信息（选填）",
          placeholder: ""
        }
      },
      imageUpload: {
        title: "分享一张让你感到焦虑的社交媒体内容截图",
        subtitle: "本工具内置匿名化处理功能，但仅适用于小红书帖子。上传其他平台内容时请自行检查，以免泄露隐私。",
        dragText: "点击或拖拽上传图片",
        formatText: "支持 JPG、PNG 格式，最大 10MB",
        successText: "收到了！AI准备和你一起看看这个内容",
        ratingPrompt: "请对这个帖子的焦虑程度进行评分",
        startButton: "让AI陪我一起看看",
        analyzing: "AI正在仔细理解这个内容...",
        clickToEnlarge: "点击放大查看",
        sampleImage: "使用示例图片",
        sampleImagesHint: "以下为已匿名化的示例图，供快速测试",
        redNotePic1: "RedNote Pic1",
        redNotePic2: "RedNote Pic2",
        tikTokPic1: "TikTok Pic1",
        tikTokPic2: "TikTok Pic2"
      },

      submitUserInfo: "保存基本信息",
      skipToUpload: "跳过，直接上传截图",
      userInfoSaving: "正在保存...",
      userInfoSaved: "基本信息已保存",
      userInfoError: "保存失败，请重试",
      userInfoRequired: "请填写必填的基本信息（性别、年龄段、职业状态，可选择不愿透露）"
    },
    results: {
      title: "请选择符合您感受的选项（可多选）",
      analysisContent: "分析内容",
      uploadedImage: "上传的帖子截图",
      anonymizedImage: "已匿名化处理的截图",
      filename: "文件名：",
      userInfo: "用户信息",
      gender: "性别：",
      age: "年龄：",
      occupation: "职业：",
      otherInfo: "其他信息：",
      hint: "AI生成的选项可能不完全符合您的感受\n为了获得更准确的建议，您可以点击'修正后采纳'进行修正\n如发现令人感到不适的内容，可点击右上角举报并反馈原因",
      acceptQuestion: "是否采纳此分析结果？",
      accept: "采纳",
      directAccept: "采纳",
      acceptAfterEdit: "修正后采纳",
      reject: "不采纳",
      acceptedTriggers: "已采纳的触发因素",
      unaccept: "取消采纳",
      feedback: {
        title: "整体反馈：",
        accuracy: "分析准确",
        clarity: "帮助理解",
        novelty: "有新认识",
        noveltyPlaceholder: "具体说明..."
      },
      submitButton: "提交并查看AI建议",
      submitting: "正在提交...",
      reanalyzeHint: "如果没有符合的选项，可选择",
      reanalyze: "重新分析",
      reanalyzing: "正在重新分析...",
      aiLimitationHint: "AI理解能力有限，如多次分析有误，请",
      tryNewImage: "换张截图"
    },
    complete: {
      title: "分析完成",
      message: "感谢完成分析。",
      newAnalysisButton: "上传新的帖子截图让AI分析",
      analyzedContent: "已分析内容",
      uploadedImage: "上传的帖子截图",
      anonymizedImage: "已匿名化处理的帖子截图",
      filename: "文件名：",
      userInfo: "用户信息",
      gender: "性别：",
      age: "年龄：",
      occupation: "职业：",
      otherInfo: "其他信息：",
      confirmedResults: "您看到这个帖子感到焦虑的原因",
      generateMoreSuggestions: "生成更多建议",
      participateInActivities: "参与活动",
      sameAsAbove: "同上",
      contactSubmitted: "您已提交联系方式",
      updating: "更新中...",
      resubmit: "重新提交",
      saving: "保存中...",
      submitContactInfo: "提交联系方式",
      updateContactInfo: "更新联系方式",
      previousQuestion: "上一问",
      completeTest: "完成测试",
      nextQuestion: "下一问",
      retakeTest: "重新测试",
      submitting: "提交中...",
      confirmNewAnalysis: "确认开始新分析",
      newAnalysisConfirmText: "是否分析新的帖子？当前的帖子数据将会被放弃。",
      startNewAnalysis: "开始新分析",
      cancel: "取消",
      clickToExitPreview: "点击图片或关闭按钮退出预览",
      uploadedImageTitle: "上传的图片",

      suggestions: {
        title: "缓解焦虑建议",
        subtitle: "基于您的感到焦虑的原因，系统为您生成三种不同类型的建议。\n系统生成的建议不是权威，如发现令人感到不适的内容，可点击右上角举报并反馈原因",
        viewSuggestions: "查看缓解建议",
        loading: "正在生成建议...",
        error: "生成建议失败，请重试",
        fromCache: "来自缓存",
        selectOtherType: "可选建议类型：",
        generating: "生成中...",
        clickToView: "点击查看建议",
        clickToGenerate: "点击生成建议",
        types: {
          cognitive_reappraisal: "换种新看法",
          problem_focus_coping: "实用行动",
          media_regulation: "管好你的信息流"
        },
        rating: {
          title: "请对这条建议的有用程度进行评分",
          scale: "1-5分"
        },
        expand: "展开",
        collapse: "收起"
      },
      evaluation: {
        title: "请对本次分析和建议进行评价",
        subtitle: "完成评价后可填写联系方式参与抽奖，有机会获得50元京东购物卡！",
        anxietyLevel: {
          title: "现在您对这个帖子的焦虑程度是多少？",
          notAnxious: "完全不焦虑",
          extremelyAnxious: "极度焦虑"
        },
        questions: {
          title: "请对以下陈述表达您的同意程度",
          scale: "1: 非常不同意; 2: 不同意; 3: 中立; 4: 同意; 5: 非常同意",
          ratingLabels: {
            1: "非常不同意",
            2: "不同意", 
            3: "中立",
            4: "同意",
            5: "非常同意"
          },
          relatability: "您觉得AI的分析结果是否帮助您理解自己的焦虑感？",
          helpfulness: "您觉得系统提供的缓解建议对您有帮助吗？",
          learnability: "通过这次的分析和建议，我学会了如何应对这类引起焦虑的内容。"
        },
        feedback: {
          title: "欢迎您分享您的想法和建议（可选）",
          placeholder: "哪个建议对您最有帮助，为什么？\n如AI的分析不准确，有什么识别或理解错了？\n如帖子引发的焦虑未能被有效缓解，原因在哪？"
        },
        suggestionChoice: {
          title: "在上述AI提供的建议，哪一种最有效地缓解了当下的焦虑感？"
        },
              contactInfo: {
        title: "参与抽奖活动",
        subtitle: "提交评价后填写联系方式即可参与，月底开奖",
        placeholder: "研究员会通过微信/邮箱直接联系您",
        hint: "请对AI工具本次分析的效果给予评价后再填写联系方式"
      },
      interview: {
        title: "后续1对1线上访谈",
        description: "您是否有意愿于本月参与1小时线上访谈，分享对AI工具分析结果的感受？",
        reward: "实验报酬：100元京东购物卡",
        placeholder: "研究员会通过微信/邮箱直接联系您",
        contactLabel: "联系方式",
        willingToParticipate: "愿意参与"
      },
      dataManagement: {
        description: "如果您误传了不想被纳入研究的截图",
        actionPrefix: "可以查看",
        historyRecord: "历史记录",
        actionSuffix: "自行删除"
      },
        submitButton: "提交评价",
        submitting: "正在提交...",
        success: "评价提交成功！",
        thankYou: "感谢您的反馈，这将帮助我们改进AI工具。",
        error: "提交失败，请重试",
        suggestionRatingHint: "请对三条建议进行评分"
      }
    },
    gad7: {
      title: '焦虑程度自测',
      instruction: '在过去两周内，以下问题困扰您的频率如何？',
      expand: '我想了解一下',
      collapse: '收起',
      waitingHint: '等待分析结果时，请进行心理状态自我测评，了解自己的焦虑水平',
      questions: {
        q1: '感到紧张、焦虑或急躁',
        q2: '无法停止或控制担忧',
        q3: '过度担心不同的事情',
        q4: '难以放松',
        q5: '坐立不安，很难静下来',
        q6: '变得容易恼怒或愤怒',
        q7: '感到恐惧，好像要发生可怕的事情'
      },
      options: {
        never: '完全没有',
        several: '有几天',
        more: '超过一半的天数',
        nearly: '几乎每天'
      },
      calculate: '计算结果',
      result: {
        title: '您的GAD-7评分：',
        mild: '轻微焦虑 (0-4分)',
        light: '轻度焦虑 (5-9分)',
        moderate: '中度焦虑 (10-14分)',
        severe: '重度焦虑 (15-21分)',
        recommendation: {
          mild: '您的焦虑水平较低，说明您目前的心理状态总体平稳。继续保持健康的生活节奏和情绪调节方式，已经做得很好了！',
          light: '您可能有轻度焦虑的表现。这是很常见的情绪状态，不用过于担心。可以尝试一些放松技巧，比如深呼吸、轻度运动或短暂冥想，帮助自己缓解紧张感。',
          moderate: '您可能存在中度焦虑，情绪状态可能已经对生活或思考产生一定影响。建议考虑向心理健康专业人士咨询，让自己获得更多理解和支持。',
          severe: '您可能正在经历较严重的焦虑状态。建议尽快联系心理健康专业人士或医生，获取专业的评估与帮助。你不是一个人，有支持资源可以陪伴你一起走过这一段。'
        }
      },
      disclaimer: '* 此测试仅供参考，不能替代专业医疗诊断'
    },

    dashboard: {
      title: "图片上传记录",
      selectAll: "全选",
      totalImages: "总图片数",
      deleteSelected: "删除选中",
      keyword: "关键词",
      triggers: "触发因素",
      uploadDate: "上传日期",
      noImages: "暂无图片",
      startAnalysis: "开始分析",
      imageDetails: "图片详情",
      loginRequired: "请先登录",
      backToHome: "返回首页",
      selectedCount: "已选择 {count} 张",
      clearSelection: "清除选择",
      deleting: "删除中...",
      noKeyword: "无关键词",
      triggersCount: "{count} 个触发因素",
      anxietyLevel: "焦虑程度",
      deselectAll: "取消全选",
      newAnalysis: "新建分析",
      deleteConfirm: {
        title: "确认删除",
        description: "删除操作不可逆，被删除的截图及其分析结果和建议将不纳入研究者的研究数据集中",
        cancel: "取消",
        confirm: "确认删除",
        deleting: "删除中..."
      },
      sort: {
        ascending: "按时间升序排列",
        descending: "按时间降序排列"
      }
    },

    anonymizationReview: {
      title: "请确认匿名化处理结果",
      hint: "请检查图片中的隐私信息是否已妥善遮挡",
      verify: "确认匿名化结果",
      reanonymize: "重新匿名化",
      waitingForAnalysis: "正在等待分析结果..."
    },

    mentalHealthSupport: {
      title: "感到焦虑、情绪低落，或想了解心理健康知识？",
      hotline: "你可以拨打全国心理援助热线 12356，免费获取专业心理支持。",
      description: "这条热线由国家卫健委设立，提供咨询、疏导与危机干预服务。",
      encouragement: "有需要，就请拨打 12356。你不是一个人。"
    },
    errors: {
      uploadRequired: "请先上传图片",
      fileSizeLimit: "图片大小不能超过10MB",
      invalidFileType: "请上传图片文件",
      sampleImageLoadFailed: "示例图片加载失败，请重试",
      analysisTimeout: "分析超时，请重试",
      analysisFailed: "分析失败",
      evaluationRequired: "请完成所有必填项的评价",
      suggestionRatingRequired: "请对建议进行评价和其他所有必填项评价",
      noCachedImage: "无缓存图片可用于重新分析",
      submissionFailed: "提交失败，请重试",
      userReportedNoReason: "用户举报但未提供具体原因",
      gad7Incomplete: "请完成所有问题后再计算结果。",
      contactRequired: "请至少填写一个联系方式。",
      contactSaveFailed: "保存联系方式失败，请重试。",
      raffleContactRequired: "请先填写抽奖活动联系方式。",
      quotaExhausted: "本邀请码分析次数已用完，继续体验请联系研究员"
    },

    common: {
      edit: "编辑",
      save: "保存",
      cancel: "取消",
      close: "关闭",
      switchToEnglish: "切换到英文",
      switchToChinese: "切换到中文",
      modify: "进行修改",
      title: "标题",
      explanation: "详细说明",
      add: "添加",
      addCustomTrigger: "手动添加其他感到焦虑的原因",
      triggerTitlePlaceholder: "请输入感到焦虑的主要原因",
      explanationPlaceholder: "请详细描述看到这个帖子感到焦虑的原因",
      anxietyLevelTitle: "这个帖子引发的焦虑程度",
      notAnxiousAtAll: "完全不焦虑",
      extremelyAnxious: "极度焦虑",
      expectedWaitTime: "预计等待时间: 约20秒",
      saving: "保存中..."
    },
    report: {
      button: "举报",
      title: "举报AI生成内容",
      question: "您是否要举报这个AI生成的内容？",
      reasonTitle: "请选择举报原因（可多选）：",
      reasons: {
        negativeEmotion: "措辞让我感到更加焦虑",
        misdiagnosis: "进行了诊断/误导",
        moreAnxious: "建议内容让我感到更加焦虑",
        preachy: "建议内容显得说教/批评",
        inappropriate: "建议涉及敏感/不适当内容",
        other: "其他（请说明）"
      },
      otherPlaceholder: "请描述其他原因...",
      submit: "提交举报",
      cancel: "返回",
      success: "举报提交成功",
      thankYou: "感谢您的反馈，这有助于进一步提升AI工具。"
    }
  },
  en: {
    app: {
      title: "Trigger Detector",
      subtitle: "anonymous@anonymous"
    },
    navigation: {
      features: "Features",
      howItWorks: "How it works",
      language: "English",
      logout: "Logout"
    },
    hero: {
      titleLoggedOut: "Does social media content often make you feel anxious?",
      subtitleLoggedOut: "Do you feel trapped in your exclusive \"anxiety information cocoon\"?\nUpload screenshots and explore your anxiety sources with AI",
      subtitle1LoggedOut: "Do you feel trapped in your exclusive \"anxiety information cocoon\"?",
      subtitle2LoggedOut: "Upload screenshots and explore your anxiety sources with AI",
      titleLoggedIn: "Does social media content often make you feel anxious?",
      subtitleLoggedIn: "Do you feel trapped in your exclusive \"anxiety information cocoon\"?\nUpload screenshots and explore your anxiety sources with AI",
      subtitle1LoggedIn: "Do you feel trapped in your exclusive \"anxiety information cocoon\"?",
      subtitle2LoggedIn: "Upload screenshots and explore your anxiety sources with AI",
      startButton: "Start Analysis"
    },
    login: {
      title: "Quick Experience",
      placeholder: "Enter experience code...",
      startButton: "Start Test",
      testKey: "Test Key",
      testExperienceCodeLabel: "Test code: ",
      copy: "Copy",
      copied: "Copied",
      copyAndFill: "Copy & Fill",
      verifying: "Verifying your experience code, please wait...",
      userNotFound: "User not found, please check your session key",
      loginFailed: "Login failed, please check your session key or network connection"
    },
    howItWorks: {
      title: "How AI accompanies you to understand feelings",
      simpleTitle: "Experience Flow",
      step1: {
        title: "Build Connection",
        titleSimple: "Login",
        description: "Share your basic information (optional) to help AI better understand your unique feelings"
      },
      step2: {
        title: "Share Concerns",
        titleSimple: "Screenshot & Upload",
        description: "Take a screenshot of troubling social media content and upload it for AI to analyze with you"
      },
      step3: {
        title: "Understand Anxiety",
        titleSimple: "Select Anxiety Triggers",
        description: "Choose anxiety triggers from AI analysis results that match your feelings"
      },
      step4: {
        title: "Get Help",
        titleSimple: "Get Relief Suggestions",
        description: "Automatically receive personalized anxiety relief suggestions and learn to better handle similar situations"
      }
    },
    userNotice: {
      title: "Welcome to Trigger Detector",
      introduction: "This tool combines your background information and uploaded social media screenshots to identify potential anxiety sources using AI technology and generates personalized emotional relief suggestions based on psychological theories.",
      process: {
        title: "Brief Usage Process:",
        step1: "Login with experience code and fill in basic information (optional)",
        step2: "Upload screenshots of social media content that makes you anxious",
        step3: "Select or adjust anxiety causes from AI analysis results that match your feelings",
        step4: "After submission, the system will display customized emotional relief suggestions for you"
      },
      privacy: {
        title: "Data Usage and Privacy Protection",
        item1: "Data will be used for research purposes, such as understanding the relationship between social media use and anxiety, improving mental health intervention tools, etc.",
        item2: "The data we collect includes your uploaded screenshots, selection results and evaluation feedback. We do not collect direct identity information such as your name. Contact information is only used for raffle activities and interview participation.",
        item3: "All data will be anonymized and securely stored on servers that comply with data protection regulations, accessible only to the research team.",
        item4: "The research project has received ethical approval from Newcastle University."
      },
      consent: {
        title: "Clicking 'Start Experience' means you:",
        item1: "Have read and understood the above instructions;",
        item2: "Agree that your provided data will be used for research purposes;",
        item3: "Clearly understand that you can withdraw at any time and stop providing information;",
        item4: "Are 18 years or older and voluntarily participate in the use and research of this tool."
      },
      startButton: "Start Experience"
    },
    analysis: {
      title: "Let AI look at this post with you",
      flowHint: "Fill info → Screenshot upload → Select anxiety triggers → Get relief suggestions",
      basicInfo: {
        title: "Basic Info",
        displayTitle: "Your Basic Information",
        autoSaveHint: "💡 Tip: Your basic info will be auto-saved when you upload a screenshot, no need to manually click save",
        loading: "Loading...",
        editButton: "Edit",
        gender: {
          label: "Gender",
          male: "Male",
          female: "Female",
          other: "Other",
          preferNotToSay: "Prefer not to say"
        },
        age: {
          label: "Age",
          placeholder: "Select age",
          "18-24": "18–24",
          "25-30": "25–30",
          "31-35": "31–35",
          "36-40": "36–40",
          "41-50": "41–50",
          "51-60": "51–60",
          "61-70": "61–70",
          "71-80": "71–80",
          "80plus": "80+",
          "preferNotToSay": "Prefer not to say"
        },
        occupation: {
          label: "Occupation",
          placeholder: "Select occupation",
          collegeStudent: "College Student",
          undergraduate: "Undergraduate",
          masterStudent: "Master's Student",
          phdStudent: "PhD Student",
          workingFullTime: "Full-time Employment",
          workingPartTime: "Part-time Employment",
          freelancer: "Freelancer",
          entrepreneur: "Entrepreneur",
          unemployed: "Unemployed",
          fullTimeParent: "Full-time Parent/Caregiver",
          retired: "Retired",
          notInLaborForce: "Not in Labor Force",
          other: "Other",
          preferNotToSay: "Prefer not to say"
        },
        mentalHealthHistory: {
          label: "Have you ever been diagnosed with mental health issues by professionals?",
          placeholder: "Please select",
          none: "Never had any",
          diagnosedAnxiety: "Previously diagnosed with anxiety disorder",
          diagnosedDepression: "Previously diagnosed with depression",
          diagnosedOther: "Previously diagnosed with other mental health issues",
          preferNotToSay: "Prefer not to say"
        },
        otherInfo: {
          label: "Other Info",
          placeholder: ""
        }
      },
      imageUpload: {
        title: "Share a social media content screenshot that makes you anxious",
        subtitle: "This tool includes built-in anonymization, but it only applies to RedNote posts. For screenshots from other platforms, please review them carefully to avoid sharing personal information.",
        dragText: "Click or drag to upload image",
        formatText: "Support JPG, PNG format, max 10MB",
        successText: "Got it! AI is ready to look at this content with you",
        ratingPrompt: "Please rate the anxiety level of this post",
        startButton: "Let AI look with me",
        analyzing: "AI is carefully understanding this content...",
        clickToEnlarge: "Click to enlarge",
        sampleImage: "Use sample image",
        sampleImagesHint: "Pre-anonymized samples for quick test",
        redNotePic1: "RedNote Pic1",
        redNotePic2: "RedNote Pic2",
        tikTokPic1: "TikTok Pic1",
        tikTokPic2: "TikTok Pic2"
      },

      submitUserInfo: "Save Basic Info",
      skipToUpload: "Skip and Upload Screenshot",
      userInfoSaving: "Saving...",
      userInfoSaved: "Basic info saved",
      userInfoError: "Save failed, please try again",
      userInfoRequired: "Please fill in required basic information (gender, age group, occupation, or select 'Prefer not to say')"
    },
    results: {
      title: "Analysis Results",
      analysisContent: "Analyzed Content",
      uploadedImage: "Uploaded Image",
      anonymizedImage: "Anonymized Post Screenshot",
      filename: "Filename: ",
      userInfo: "User Information",
      gender: "Gender: ",
      age: "Age: ",
      occupation: "Occupation: ",
      otherInfo: "Other Info: ",
      hint: "AI-generated options may not completely match your feelings\nTo get more accurate suggestions, you can click 'Accept After Edit' to make corrections\nIf you find content that makes you uncomfortable, you can click the report button in the top right corner and provide feedback",
      acceptQuestion: "Accept this analysis result?",
      accept: "Accept",
      directAccept: "Accept",
      acceptAfterEdit: "Accept After Edit",
      reject: "Reject",
      acceptedTriggers: "Accepted Triggers",
      unaccept: "Unaccept",
      feedback: {
        title: "Overall Feedback: ",
        accuracy: "Analysis Accurate",
        clarity: "Helps Understanding",
        novelty: "New Insights",
        noveltyPlaceholder: "Please specify..."
      },
      submitButton: "Submit Accurate Result",
      submitting: "Submitting...",
      reanalyzeHint: "If no options match, you can click",
      reanalyze: "Reanalyze",
      reanalyzing: "Reanalyzing...",
      aiLimitationHint: "AI has limited understanding. If analysis is repeatedly incorrect, please",
      tryNewImage: "try a different screenshot"
    },
    complete: {
      title: "Analysis Complete",
      message: "Thank you for completing the analysis.",
      newAnalysisButton: "Analyze New Content",
      analyzedContent: "Analyzed Content",
      uploadedImage: "Uploaded Image",
      anonymizedImage: "Anonymized Post Screenshot",
      filename: "Filename: ",
      userInfo: "User Information",
      gender: "Gender: ",
      age: "Age: ",
      occupation: "Occupation: ",
      otherInfo: "Other info: ",
      confirmedResults: "Your Confirmed Accurate Results",
      generateMoreSuggestions: "Generate More Suggestions",
      participateInActivities: "Participate in Activities",
      sameAsAbove: "Same as above",
      contactSubmitted: "Contact information submitted",
      updating: "Updating...",
      resubmit: "Resubmit",
      saving: "Saving...",
      submitContactInfo: "Submit Contact Information",
      updateContactInfo: "Update Contact Information",
      previousQuestion: "Previous",
      completeTest: "Complete",
      nextQuestion: "Next",
      retakeTest: "Retake Test",
      submitting: "Submitting...",
      confirmNewAnalysis: "Confirm New Analysis",
      newAnalysisConfirmText: "Analyze new post? Current post data will be discarded.",
      startNewAnalysis: "Yes, Start New Analysis",
      cancel: "Cancel",
      clickToExitPreview: "Click image or close button to exit preview",
      uploadedImageTitle: "Uploaded Image",

      suggestions: {
        title: "Anxiety Relief Suggestions",
        subtitle: "Based on your anxiety triggers, the system provides three types of suggestions.\nThese are not authoritative. If any content feels inappropriate, please report it via the top right corner.",
        instruction: "The system automatically generates three different types of anxiety relief suggestions. Each suggestion takes about 10 seconds to generate, please be patient.",
        viewSuggestions: "View Relief Suggestions",
        loading: "Generating suggestions...",
        error: "Failed to generate suggestions, please try again",
        fromCache: "From cache",
        selectOtherType: "Select other suggestion type:",
        generating: "Generating...",
        clickToView: "Click to view suggestion",
        clickToGenerate: "Click to generate suggestion",
        types: {
          cognitive_reappraisal: "New Ways to See it",
          problem_focus_coping: "Practical Steps",
          media_regulation: "Manage Your Feed"
        },
        rating: {
          title: "Rating",
          scale: "1-5"
        },
        expand: "Expand",
        collapse: "Collapse"
      },
      evaluation: {
        title: "Evaluation & Feedback",
        subtitle: "Please evaluate this analysis and suggestions",
        anxietyLevel: {
          title: "Current Anxiety Level",
          notAnxious: "Not Anxious at All",
          extremelyAnxious: "Extremely Anxious"
        },
        questions: {
          title: "How strongly do you agree or disagree with the following statements?",
          scale: "1: Strongly Disagree; 2: Disagree; 3: Neutral; 4: Agree; 5: Strongly Agree",
          ratingLabels: {
            1: "Strongly Disagree",
            2: "Disagree",
            3: "Neutral", 
            4: "Agree",
            5: "Strongly Agree"
          },
          relatability: "Do you think the anxiety triggers identified by the system are accurate?",
          helpfulness: "Do you find the anxiety relief suggestions provided by the system helpful?",
          learnability: "By doing this activity, I learn how I can deal with this kind of anxiety-inducing content in the future."
        },
        feedback: {
          title: "Comment",
          placeholder: "Please share your thoughts and suggestions..."
        },
        suggestionChoice: {
          title: "Which of the AI suggestions above helped ease your anxiety the most?"
        },
        contactInfo: {
          title: "Join Lucky Draw",
          subtitle: "Fill in your contact information to participate in the lucky draw for a chance to win a 50 yuan JD gift card!",
          placeholder: "Please enter your contact information (phone number or email)",
          hint: "Please rate the effectiveness of the AI tool before filling in your contact information"
        },
        interview: {
          title: "Participate in In-depth Interview",
          description: "Would you like to participate in a 1-hour interview to share your thoughts on the AI tool's analysis results?",
          reward: "Experiment compensation: 100 yuan JD gift card",
          placeholder: "Please enter your contact information (phone number or email)",
          contactLabel: "Contact Information",
          willingToParticipate: "Willing to participate"
        },
        dataManagement: {
          description: "If you uploaded a screenshot that you don't want to be included in the research",
          actionPrefix: "you can view",
          historyRecord: "History",
          actionSuffix: "and delete it yourself"
        },
        submitButton: "Submit Evaluation",
        submitting: "Submitting...",
        success: "Evaluation submitted successfully!",
        thankYou: "Thank you for your feedback, it will help us improve our AI tool.",
        error: "Submission failed, please try again",
        suggestionRatingHint: "Please rate all three suggestions"
      }
    },
    gad7: {
      title: 'Anxiety Self-Assessment',
      instruction: 'How often have you been bothered by the following over the past 2 weeks?',
      expand: 'Expand',
      collapse: 'Collapse Assessment',
      waitingHint: 'While waiting for analysis results, you are welcome to take a mental health self-assessment to understand your anxiety level. (Optional)',
      questions: {
        q1: 'Feeling nervous, anxious, or on edge',
        q2: 'Not being able to stop or control worrying',
        q3: 'Worrying too much about different things',
        q4: 'Trouble relaxing',
        q5: 'Being so restless that it\'s hard to sit still',
        q6: 'Becoming easily annoyed or irritable',
        q7: 'Feeling afraid as if something awful might happen'
      },
      options: {
        never: 'Not at all',
        several: 'Several days',
        more: 'More than half the days',
        nearly: 'Nearly every day'
      },
      calculate: 'Calculate Result',
      result: {
        title: 'Your GAD-7 Score:',
        mild: 'Minimal Anxiety (0-4 points)',
        light: 'Mild Anxiety (5-9 points)',
        moderate: 'Moderate Anxiety (10-14 points)',
        severe: 'Severe Anxiety (15-21 points)',
        recommendation: {
          mild: 'Your anxiety level is minimal. Continue maintaining healthy lifestyle habits.',
          light: 'You may have mild anxiety. Try relaxation techniques such as deep breathing and exercise.',
          moderate: 'You may have moderate anxiety. Consider consulting with a mental health professional for guidance and support.',
          severe: 'You may have severe anxiety. Strongly recommend consulting with a mental health professional or doctor for professional diagnosis and treatment advice.'
        }
      },
      disclaimer: '* This assessment is for reference only and cannot replace professional medical diagnosis'
    },

    dashboard: {
      title: "Image Upload History",
      selectAll: "Select All",
      totalImages: "Total Images",
      deleteSelected: "Delete Selected",
      keyword: "Keyword",
      triggers: "Triggers",
      uploadDate: "Upload Date",
      noImages: "No images yet",
      startAnalysis: "Start Analysis",
      imageDetails: "Image Details",
      loginRequired: "Please login first",
      backToHome: "Back to Home",
      selectedCount: "Selected {count}",
      clearSelection: "Clear Selection",
      deleting: "Deleting...",
      noKeyword: "No keyword",
      triggersCount: "{count} triggers",
      anxietyLevel: "Anxiety Level",
      deselectAll: "Deselect All",
      newAnalysis: "New Analysis",
      deleteConfirm: {
        title: "Confirm Delete",
        description: "This action cannot be undone. Deleted screenshots and their analysis results will not be included in the researcher's dataset.",
        cancel: "Cancel",
        confirm: "Confirm Delete",
        deleting: "Deleting..."
      },
      sort: {
        ascending: "Sort by ascending time",
        descending: "Sort by descending time"
      }
    },

    anonymizationReview: {
      title: "Please verify the anonymization result",
      hint: "Check whether personal information has been properly masked",
      verify: "Accept anonymization",
      reanonymize: "Re-anonymize",
      waitingForAnalysis: "Waiting for analysis results..."
    },

    mentalHealthSupport: {
      title: "Feeling anxious, depressed, or want to learn about mental health?",
      hotline: "You can call the National Mental Health Support Hotline 12356 for free professional psychological support.",
      description: "This hotline is established by the National Health Commission, providing consultation, counseling and crisis intervention services.",
      encouragement: "If you need help, please call 12356. You are not alone."
    },
    errors: {
      uploadRequired: "Please upload an image first",
      fileSizeLimit: "Image size should be less than 10MB",
      invalidFileType: "Please upload an image file",
      sampleImageLoadFailed: "Failed to load sample image, please try again",
      analysisTimeout: "Analysis timeout, please try again",
      analysisFailed: "Analysis failed",
      evaluationRequired: "Please complete all rating items",
      suggestionRatingRequired: "Please rate all suggestions and complete all required evaluation items",
      noCachedImage: "No cached image available for reanalysis",
      submissionFailed: "Submission failed, please try again",
      userReportedNoReason: "User reported without specific reason",
      gad7Incomplete: "Please complete all questions before calculating the result.",
      contactRequired: "Please provide at least one contact method.",
      contactSaveFailed: "Failed to save contact information. Please try again.",
      raffleContactRequired: "Please fill in the raffle contact information first.",
      quotaExhausted: "Analysis quota exhausted. Please contact administrator for more quota."
    },

    common: {
      edit: "Edit",
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      switchToEnglish: "Switch to English",
      switchToChinese: "Switch to Chinese",
      modify: "Modify",
      title: "Title",
      explanation: "Explanation",
      add: "Add",
      addCustomTrigger: "Add Custom Trigger",
      triggerTitlePlaceholder: "Enter trigger title",
      explanationPlaceholder: "Please describe this trigger in detail",
      anxietyLevelTitle: "Anxiety Level Triggered by This Post",
      notAnxiousAtAll: "Not Anxious at All",
      extremelyAnxious: "Extremely Anxious",
      expectedWaitTime: "Expected wait time: ~20 seconds",
      saving: "Saving..."
    },
    report: {
      button: "Report",
      title: "Report AI Generated Content",
      question: "Do you want to report this AI-generated content?",
      reasonTitle: "Please select the reason(s) for reporting (multiple selections allowed):",
      reasons: {
        negativeEmotion: "The wording made me feel even more anxious",
        misdiagnosis: "The analysis includes a diagnosis or is misleading",
        moreAnxious: "The suggestion content makes me feel more anxious",
        preachy: "The suggestion content seems preachy/critical",
        inappropriate: "The suggestion involves sensitive/inappropriate content",
        other: "Other (please specify)"
      },
      otherPlaceholder: "Please describe other reasons...",
      submit: "Submit Report",
      cancel: "Cancel",
      success: "Report submitted successfully",
      thankYou: "Thank you for your feedback, this helps us improve our AI tool."
    }
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<'zh' | 'en'>('zh')

  // 从localStorage加载语言设置，优先考虑用户信息中的语言设置
  useEffect(() => {
    // 首先检查是否有缓存的用户信息
    const cachedUserInfo = localStorage.getItem('cachedUserInfo')
    if (cachedUserInfo) {
      try {
        const userInfo = JSON.parse(cachedUserInfo)
        if (userInfo.language && (userInfo.language === 'zh' || userInfo.language === 'en')) {
          console.log('[I18nProvider] Setting language from cached user info:', userInfo.language)
          setLanguage(userInfo.language)
          // 同时更新localStorage中的语言设置
          localStorage.setItem('language', userInfo.language)
          return
        }
      } catch (e) {
        console.error('[I18nProvider] Failed to parse cached user info:', e)
      }
    }
    
    // 如果没有用户信息，则从localStorage加载通用语言设置
    const savedLanguage = localStorage.getItem('language') as 'zh' | 'en'
    if (savedLanguage && (savedLanguage === 'zh' || savedLanguage === 'en')) {
      console.log('[I18nProvider] Setting language from localStorage:', savedLanguage)
      setLanguage(savedLanguage)
    }
  }, [])

  // 优化：避免在初始化时重复设置localStorage
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }
    
    // 只有在非初始化状态下才更新localStorage
    localStorage.setItem('language', language);
  }, [language, isInitialized]);



  // 翻译函数
  const t = (key: string): string => {
    const keys = key.split('.')
    let value: any = translations[language]
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        console.warn(`Translation key not found: ${key}`)
        return key
      }
    }
    
    return typeof value === 'string' ? value : key
  }

  const setLanguageWithCallback = (lang: 'zh' | 'en', callback?: () => void) => {
    setLanguage(lang);
    if (callback) {
      callback();
    }
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage: setLanguageWithCallback, t }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return context
} 